import { useState, useEffect } from 'react';
import { 
  Flex,
  View,
  Text,
  Heading,
  Button,
  Card,
  Grid,
  Loader
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { list } from 'aws-amplify/storage';
import awsConfig from '../aws-exports';
import ListingsDisplay from './ListingsDisplay';

const HomePage = ({ userAttributes }) => {
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentScore, setCurrentScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cvFileName, setCvFileName] = useState(null);
  const [cvFeedback, setCvFeedback] = useState(null);
  const [fileCount, setFileCount] = useState(0);
  
  // State variables for user stats API
  const [savedJobsCount, setSavedJobsCount] = useState(0);
  const [topScoredJobs, setTopScoredJobs] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Function to format feedback text with proper line breaks
  const formatFeedbackText = (text) => {
    if (!text) return '';
    
    let formatted = text
      .replace(/\. /g, '.\n\n')
      .replace(/\.\n/g, '.\n\n')
      .replace(/\n\n\n+/g, '\n\n')
      .trim();
    
    if (formatted.includes('keywords') || formatted.includes('Keywords')) {
      formatted = formatted
        .replace(/; /g, ';\n\n')
        .replace(/: /g, ':\n\n')
        .replace(/\n\n\n+/g, '\n\n');
    }
    
    return formatted;
  };
  
  // Function to reset to default state when no CV is available
  const resetToDefaults = () => {
    setCurrentScore(0);
    setCvFileName(null);
    setCvFeedback(null);
  };

  // Function to fetch actual file count from S3
  const fetchFileCount = async () => {
    try {
      const result = await list({
        options: {
          accessLevel: 'private'
        }
      });
      
      const count = result.items ? result.items.length : 0;
      setFileCount(count);
      console.log(`File count updated: ${count}`);
    } catch (error) {
      console.error('Error fetching file count:', error);
      setFileCount(0);
    }
  };
  
  // Function to fetch user stats from API
  const fetchUserStats = async () => {
    try {
      setLoadingStats(true);
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        console.log('No authentication token available for user stats');
        setLoadingStats(false);
        return;
      }
      
      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'user_stats'
      );
      
      if (!apiConfig) {
        console.log('User stats API not configured');
        setLoadingStats(false);
        return;
      }
      
      const apiEndpoint = `${apiConfig.endpoint}/user-stats`;
      
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch user stats:', response.status);
        setLoadingStats(false);
        return;
      }
      
      const data = await response.json();
      console.log('User stats data:', data);
      
      if (data.savedJobs && typeof data.savedJobs.totalCount === 'number') {
        setSavedJobsCount(data.savedJobs.totalCount);
      }
      
      if (data.cvAnalysis && Array.isArray(data.cvAnalysis.topScored)) {
        setTopScoredJobs(data.cvAnalysis.topScored);
        console.log('TopScoredJobs structure:', data.cvAnalysis.topScored);
      }
      
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Fetch latest CV analysis result - UPDATED TO REMOVE CACHING
  const fetchLatestCVScore = async () => {
    try {
      setLoading(true);
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        console.log('No authentication token available');
        resetToDefaults(); // Always reset, no fallback
        setLoading(false);
        return;
      }
      
      const apiConfig = awsConfig.aws_cloud_logic_custom.find(
        api => api.name === 'cv_analysis_results'
      );
      
      if (!apiConfig) {
        console.log('CV analysis results API not configured');
        resetToDefaults(); // Always reset, no fallback
        setLoading(false);
        return;
      }
      
      const apiEndpoint = `${apiConfig.endpoint}/results?latest=true`;
      
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': idToken.toString(),
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.log('No CV analysis found from API');
        resetToDefaults(); // Always reset when API says no data
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('CV Analysis API Response:', data); // Debug log
      
      if (data.success && data.results && data.results.score > 0) {
        // Only set state from API data, no localStorage saving
        console.log('Setting score:', data.results.score); // Debug log
        setCurrentScore(data.results.score || 0);
        setCvFileName(data.results.fileName || null);
        setCvFeedback({
          ...data.results.feedback,
          score: data.results.score || 0,
          fileName: data.results.fileName || null
        });
      } else {
        console.log('No valid results from API - data structure:', data);
        resetToDefaults(); // Reset if no valid data
      }
      
    } catch (err) {
      console.error('Error fetching CV score:', err);
      resetToDefaults(); // Reset on any error
    } finally {
      setLoading(false);
    }
  };

  // Get current user and set up user-specific data
  useEffect(() => {
    const getCurrentUserData = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUserId(user.userId);
      } catch (error) {
        console.error('Error getting current user:', error);
        setLoading(false);
      }
    };
    
    getCurrentUserData();
  }, []);

  // Load user data when currentUserId changes - UPDATED
  useEffect(() => {
    if (currentUserId) {
      console.log('User ID set, fetching all data...');
      // Don't check localStorage, just fetch from API
      resetToDefaults(); // Start fresh
      fetchFileCount();
      fetchUserStats();
      fetchLatestCVScore(); // This should work just like fetchUserStats
    }
    // fetchers are component-scoped and re-created every render; re-running
    // them only when currentUserId changes is the intended behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Refresh user stats when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      if (currentUserId) {
        console.log('Window focused - refreshing data');
        fetchFileCount();
        fetchUserStats();
        fetchLatestCVScore(); // Call it exactly like fetchUserStats
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Listen for feedback update events only - UPDATED
  useEffect(() => {
    const handleFeedbackUpdate = () => {
      if (currentUserId) {
        console.log('Feedback event triggered - refreshing data');
        // Only fetch from API, no localStorage
        fetchFileCount();
        fetchUserStats();
        fetchLatestCVScore(); // Call it exactly like fetchUserStats
      }
    };

    window.addEventListener('cvFeedbackUpdated', handleFeedbackUpdate);

    return () => {
      window.removeEventListener('cvFeedbackUpdated', handleFeedbackUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const handleImprove = () => {
    navigate('/CvAssistance');
  };

  const handleViewAllTips = () => {
    navigate('/CvAssistance');
  };

  const activityStats = {
    cvUploads: fileCount,
    savedJobs: savedJobsCount
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#2ECC71';
    if (score >= 60) return '#F39C12';
    return '#E74C3C';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score > 0) return 'Needs Work';
    return 'No CV Uploaded';
  };

  return (
    <View style={{ position: 'relative', overflow: 'hidden', paddingBottom: '20px' }}>
      {/* Full page animated background */}
      <View
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1
        }}
      >
        <View
          style={{
            position: 'absolute',
            top: '-100px',
            left: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '-150px',
            right: '-150px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />
        <View
          style={{
            position: 'absolute',
            top: '40%',
            left: '10%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            animation: 'float 10s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '30%',
            right: '20%',
            width: '350px',
            height: '350px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
            animation: 'float 12s ease-in-out infinite reverse'
          }}
        />
        <View
          style={{
            position: 'absolute',
            top: '60%',
            right: '40%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.09)',
            animation: 'float 15s ease-in-out infinite'
          }}
        />
      </View>

      <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
        <View maxWidth="1400px" margin="0 auto">
          {/* Welcome Card */}
          <Card 
            borderRadius="12px" 
            padding="2.5rem" 
            margin="0 0 2rem 0"
            style={{
              background: 'rgba(30, 35, 45, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Flex direction="column" gap="0.5rem">
                <Heading level={2} style={{ color: 'white' }}>
                  Welcome back, {userAttributes?.given_name || 'User'}!
                </Heading>
                <Text fontSize="1.1rem" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  Your career journey at a glance
                </Text>
              </Flex>
              
              <Card 
                borderRadius="12px"
                padding="1.5rem"
                backgroundColor="rgba(255,255,255,0.05)"
                width="320px"
                style={{ 
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <Flex direction="column" gap="1rem">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text fontWeight="bold" fontSize="1.1rem" color="white">Latest ATS Score</Text>
                    <View 
                      backgroundColor={currentScore > 0 ? getScoreColor(currentScore) : '#667eea'}
                      padding="0.25rem 0.75rem"
                      borderRadius="20px"
                    >
                      <Text color="white" fontSize="0.8rem" fontWeight="bold">
                        {getScoreLabel(currentScore)}
                      </Text>
                    </View>
                  </Flex>
                  
                  {loading && !cvFeedback ? (
                    <Flex justifyContent="center" alignItems="center" height="60px">
                      <Loader size="large" />
                    </Flex>
                  ) : (
                    <Flex direction="column" gap="0.5rem">
                      <View 
                        backgroundColor="rgba(255,255,255,0.1)" 
                        height="12px" 
                        borderRadius="6px"
                        overflow="hidden"
                      >
                        <View 
                          backgroundColor={currentScore > 0 ? getScoreColor(currentScore) : 'rgba(255,255,255,0.1)'}
                          height="12px" 
                          width={`${currentScore}%`}
                          style={{
                            borderRadius: '6px',
                            transition: 'width 0.5s ease-in-out'
                          }}
                        />
                      </View>
                      <Flex justifyContent="space-between" alignItems="center">
                        <Flex alignItems="baseline" gap="0.25rem">
                          <Text fontWeight="bold" fontSize="1.5rem" color="white">{currentScore}</Text>
                          <Text fontSize="1rem" color="rgba(255,255,255,0.7)">/100</Text>
                        </Flex>
                        <Button
                          variation="link"
                          onClick={handleImprove}
                          style={{ 
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            textDecoration: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            transition: 'all 0.3s',
                            boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 3px 10px rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          {currentScore > 0 ? 'Improve →' : 'Upload CV →'}
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                  
                  {cvFileName && (
                    <Text fontSize="0.8rem" color="rgba(255,255,255,0.6)" textAlign="center">
                      Last analysed: {cvFileName}
                    </Text>
                  )}
                </Flex>
              </Card>
            </Flex>
          </Card>

          {/* Two-column layout */}
          <Grid
            templateColumns={{
              base: "1fr",
              large: "1fr 2fr"
            }}
            gap="2rem"
          >
            {/* Left Column */}
            <Flex direction="column" gap="2rem">
              {/* Most Compatible Jobs - NO WRAPPER, let ListingsDisplay handle its own Card */}
              <ListingsDisplay 
                variant="compact"
                maxItems={5}
                showPagination={false}
                showSorting={false}
                showFilters={false}
                preloadedJobs={topScoredJobs}
                loadingJobs={loadingStats}
                cvFileName={cvFileName}  // Pass CV filename to display
              />

              {/* Scoring Guide Card - Always visible */}
              <Card 
                borderRadius="12px" 
                padding="1.5rem"
                style={{ 
                  background: 'rgba(30, 35, 45, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <Flex direction="column" gap="1rem">
                  <Heading level={4} style={{ color: 'white', fontSize: '1.1rem' }}>
                    Scoring Guide
                  </Heading>
                  
                  <Text fontSize="0.85rem" color="rgba(255,255,255,0.6)" marginBottom="0.5rem">
                    Understanding your ATS compatibility scores
                  </Text>
                  
                  <Flex direction="column" gap="0.75rem">
                    {/* Excellent Range */}
                    <Card
                      backgroundColor="rgba(46, 204, 113, 0.1)"
                      borderRadius="8px"
                      padding="0.75rem"
                      style={{
                        border: '1px solid rgba(46, 204, 113, 0.2)'
                      }}
                    >
                      <Flex alignItems="flex-start" gap="0.75rem">
                        <View 
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#2ECC71',
                            flexShrink: 0,
                            marginTop: '2px'
                          }}
                        />
                        <Flex direction="column" gap="0.25rem" flex="1">
                          <Text fontSize="0.9rem" color="white" fontWeight="600">
                            80-100: Excellent
                          </Text>
                          <Text fontSize="0.8rem" color="rgba(255,255,255,0.7)">
                            Your CV is highly optimised. Strong keyword match, clear structure, and meets ATS requirements. You're ready to apply!
                          </Text>
                        </Flex>
                      </Flex>
                    </Card>

                    {/* Good Range */}
                    <Card
                      backgroundColor="rgba(243, 156, 18, 0.1)"
                      borderRadius="8px"
                      padding="0.75rem"
                      style={{
                        border: '1px solid rgba(243, 156, 18, 0.2)'
                      }}
                    >
                      <Flex alignItems="flex-start" gap="0.75rem">
                        <View 
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#F39C12',
                            flexShrink: 0,
                            marginTop: '2px'
                          }}
                        />
                        <Flex direction="column" gap="0.25rem" flex="1">
                          <Text fontSize="0.9rem" color="white" fontWeight="600">
                            60-79: Good
                          </Text>
                          <Text fontSize="0.8rem" color="rgba(255,255,255,0.7)">
                            Solid foundation with room for improvement. Consider adding more relevant keywords and refining sections.
                          </Text>
                        </Flex>
                      </Flex>
                    </Card>

                    {/* Needs Work Range */}
                    <Card
                      backgroundColor="rgba(231, 76, 60, 0.1)"
                      borderRadius="8px"
                      padding="0.75rem"
                      style={{
                        border: '1px solid rgba(231, 76, 60, 0.2)'
                      }}
                    >
                      <Flex alignItems="flex-start" gap="0.75rem">
                        <View 
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#E74C3C',
                            flexShrink: 0,
                            marginTop: '2px'
                          }}
                        />
                        <Flex direction="column" gap="0.25rem" flex="1">
                          <Text fontSize="0.9rem" color="white" fontWeight="600">
                            0-59: Needs Work
                          </Text>
                          <Text fontSize="0.8rem" color="rgba(255,255,255,0.7)">
                            Major improvements needed. Focus on structure, keywords, and clarity. Use our CV assistance to boost your score.
                          </Text>
                        </Flex>
                      </Flex>
                    </Card>
                  </Flex>

                  <Text fontSize="0.75rem" color="rgba(255,255,255,0.5)" textAlign="center" marginTop="0.5rem">
                    These colours apply to both CV scores and job compatibility ratings
                  </Text>
                </Flex>
              </Card>
            </Flex>
            
            {/* Right Column - Activity and AI Feedback */}
            <Flex direction="column" gap="2rem">
              {/* Activity Card */}
              <Card 
                borderRadius="12px" 
                padding="1.5rem" 
                style={{ 
                  background: 'rgba(30, 35, 45, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <View style={{
                  position: 'absolute',
                  left: '50px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  opacity: 0.08,
                  fontSize: '80px'
                }}>
                  📋
                </View>

                <View style={{
                  position: 'absolute',
                  right: '50px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  opacity: 0.08,
                  fontSize: '80px'
                }}>
                  💼
                </View>

                <Flex direction="column" gap="1rem" position="relative" zIndex="1">
                  <Heading level={3} style={{ color: 'white', fontSize: '1.3rem' }}>Your Activity</Heading>
                  
                  <Flex 
                    gap="2rem"
                    justifyContent="center"
                    alignItems="center"
                    padding="1rem 0"
                  >
                    <Flex 
                      direction="column" 
                      alignItems="center" 
                      gap="0.5rem"
                      style={{
                        padding: '1.5rem 2.5rem',
                        background: 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(46, 204, 113, 0.2) 100%)',
                        borderRadius: '15px',
                        border: '2px solid rgba(46, 204, 113, 0.3)',
                        position: 'relative',
                        minWidth: '160px'
                      }}
                    >
                      <View 
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 5px 15px rgba(46, 204, 113, 0.3)'
                        }}
                      >
                        <Text fontSize="1.5rem">📄</Text>
                      </View>
                      <View style={{ textAlign: 'center' }}>
                        <Text fontWeight="bold" fontSize="2rem" color="white" style={{ lineHeight: '1' }}>
                          {activityStats.cvUploads}
                        </Text>
                        <Text color="rgba(255,255,255,0.8)" fontSize="0.95rem" marginTop="0.25rem">
                          CV Uploads
                        </Text>
                        <Text color="rgba(255,255,255,0.5)" fontSize="0.75rem" marginTop="0.25rem">
                          {activityStats.cvUploads === 0 ? 'Start your journey' : 'Keep improving'}
                        </Text>
                      </View>
                    </Flex>

                    <View 
                      style={{
                        width: '1px',
                        height: '60px',
                        background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.15), transparent)',
                      }}
                    />

                    <Flex 
                      direction="column" 
                      alignItems="center" 
                      gap="0.5rem"
                      style={{
                        padding: '1.5rem 2.5rem',
                        background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(243, 156, 18, 0.2) 100%)',
                        borderRadius: '15px',
                        border: '2px solid rgba(243, 156, 18, 0.3)',
                        position: 'relative',
                        minWidth: '160px'
                      }}
                    >
                      <View 
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 5px 15px rgba(243, 156, 18, 0.3)'
                        }}
                      >
                        <Text fontSize="1.5rem">⭐</Text>
                      </View>
                      <View style={{ textAlign: 'center' }}>
                        <Text fontWeight="bold" fontSize="2rem" color="white" style={{ lineHeight: '1' }}>
                          {loadingStats ? '...' : activityStats.savedJobs}
                        </Text>
                        <Text color="rgba(255,255,255,0.8)" fontSize="0.95rem" marginTop="0.25rem">
                          Saved Jobs
                        </Text>
                        <Text color="rgba(255,255,255,0.5)" fontSize="0.75rem" marginTop="0.25rem">
                          {activityStats.savedJobs === 0 ? 'Explore opportunities' : 'Ready to apply'}
                        </Text>
                      </View>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            
              {/* AI Feedback Card */}
              <Card 
                borderRadius="12px" 
                padding="2rem"
                style={{ 
                  background: 'rgba(30, 35, 45, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <Flex direction="column" gap="1.5rem">
                  <Heading level={3} style={{ color: 'white' }}>AI Feedback</Heading>
                  
                  <Flex direction="column" gap="1rem">
                    {loading && !cvFeedback ? (
                      <Flex justifyContent="center" alignItems="center" height="120px">
                        <Loader size="large" />
                      </Flex>
                    ) : cvFeedback && (cvFeedback.structure || cvFeedback.content || cvFeedback.keywords) ? (
                      <>
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(52, 152, 219, 0.15)"
                          border="1px solid rgba(52, 152, 219, 0.3)"
                          style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                          onClick={handleViewAllTips}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                          }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                              <Text fontSize="1.2rem">📋</Text>
                              <Text color="#67b8ff" fontWeight="bold">Structure:</Text>
                            </Flex>
                            <Text 
                              color="rgba(255,255,255,0.7)" 
                              fontSize="0.9rem"
                              style={{
                                whiteSpace: 'pre-line',
                                textAlign: 'center',
                                lineHeight: '1.5'
                              }}
                            >
                              {formatFeedbackText(cvFeedback.structure || 'Your CV structure will be analysed...')}
                            </Text>
                          </Flex>
                        </Card>
                        
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(52, 152, 219, 0.15)"
                          border="1px solid rgba(52, 152, 219, 0.3)"
                          style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                          onClick={handleViewAllTips}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                          }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                              <Text fontSize="1.2rem">📝</Text>
                              <Text color="#67b8ff" fontWeight="bold">Content:</Text>
                            </Flex>
                            <Text 
                              color="rgba(255,255,255,0.7)" 
                              fontSize="0.9rem"
                              style={{
                                whiteSpace: 'pre-line',
                                textAlign: 'center',
                                lineHeight: '1.5'
                              }}
                            >
                              {formatFeedbackText(cvFeedback.content || 'Content quality assessment will appear here...')}
                            </Text>
                          </Flex>
                        </Card>
                        
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(52, 152, 219, 0.15)"
                          border="1px solid rgba(52, 152, 219, 0.3)"
                          style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                          onClick={handleViewAllTips}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                          }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                              <Text fontSize="1.2rem">🔍</Text>
                              <Text color="#67b8ff" fontWeight="bold">Keywords:</Text>
                            </Flex>
                            <Text 
                              color="rgba(255,255,255,0.7)" 
                              fontSize="0.9rem"
                              style={{
                                whiteSpace: 'pre-line',
                                textAlign: 'center',
                                lineHeight: '1.5'
                              }}
                            >
                              {formatFeedbackText(cvFeedback.keywords || 'Industry keywords and ATS optimisation tips...')}
                            </Text>
                          </Flex>
                        </Card>
                      </>
                    ) : (
                      <>
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(52, 152, 219, 0.15)"
                          border="1px solid rgba(52, 152, 219, 0.3)"
                          style={{ transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                          }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                              <Text fontSize="1.2rem">💡</Text>
                              <Text color="#67b8ff" fontWeight="bold">Upload your CV</Text>
                            </Flex>
                            <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem" style={{ textAlign: 'center' }}>Get personalised AI feedback on structure, content, and keywords</Text>
                          </Flex>
                        </Card>
                        
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(52, 152, 219, 0.15)"
                          border="1px solid rgba(52, 152, 219, 0.3)"
                          style={{ transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                          }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                              <Text fontSize="1.2rem">📊</Text>
                              <Text color="#67b8ff" fontWeight="bold">ATS Optimisation</Text>
                            </Flex>
                            <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem" style={{ textAlign: 'center' }}>Improve your CV's compatibility with Applicant Tracking Systems</Text>
                          </Flex>
                        </Card>
                        
                        <Card 
                          borderRadius="10px" 
                          padding="1.5rem" 
                          backgroundColor="rgba(52, 152, 219, 0.15)"
                          border="1px solid rgba(52, 152, 219, 0.3)"
                          style={{ transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.backgroundColor = 'rgba(52, 152, 219, 0.15)';
                          }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
                              <Text fontSize="1.2rem">🎯</Text>
                              <Text color="#67b8ff" fontWeight="bold">Industry Keywords</Text>
                            </Flex>
                            <Text color="rgba(255,255,255,0.7)" fontSize="0.9rem" style={{ textAlign: 'center' }}>Match your CV with industry-specific requirements</Text>
                          </Flex>
                        </Card>
                      </>
                    )}
                    
                    <Button
                      variation="primary"
                      onClick={handleViewAllTips}
                      width="100%"
                      fontSize="1rem"
                      fontWeight="600"
                      marginTop="0.5rem"
                      style={{
                        borderRadius: '8px',
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: 'white',
                        transition: 'all 0.3s',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                      }}
                    >
                      {cvFeedback ? 'Upload & Get New Analysis' : 'Upload CV & Get Analysis'}
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            </Flex>
          </Grid>
        </View>
      </View>

      <style jsx>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-30px);
          }
          100% {
            transform: translateY(0px);
          }
        }
      `}</style>
    </View>
  );
};

export default HomePage;